/** @jsx jsx */
import { useState, useEffect, useRef } from 'react'
import { keyframes } from '@emotion/react'
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  jsx,
  Text,
} from 'theme-ui'
import { Link } from 'gatsby'
import { toUsdCurrency } from '~/utils/formatters'
import { getFormattedBottleCount } from '~/utils/products'
import CheckmarkIcon from '~/assets/images/icons/checkmark.inline.svg'
import CheckmarkThinIcon from '~/assets/images/icons/checkmark-thin.inline.svg'

const ChooseMembershipSection = ({ title, media, footer }) => {
  const anchorRef = useRef(null)

  const handleCtaClick = (e) => {
    e.preventDefault()
    anchorRef.current.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const joinLink = document.querySelector('a[href="#join"]')

    if (joinLink) {
      // Smooth scroll from join button to choose membership anchor
      joinLink.removeEventListener('click', handleCtaClick)
      joinLink.addEventListener('click', handleCtaClick)
    }
  }, [])

  const membershipRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  const callback = (entry) => {
    if (entry[0].isIntersecting) {
      setIsVisible(true)
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(callback, { threshold: 0.2 })
    const ref = membershipRef.current
    observer.observe(ref)

    return () => {
      observer.unobserve(ref)
    }
  })

  const slide = keyframes`
    from {
      opacity: 0;
      transform: translate(-20%, 0) rotate(-15deg);
    }
    to {
      opacity: 1;
      transform: translate(0);
    }
    `

  return (
    <Box
      bg="backgroundLight"
      pb={[11, null, null, 14]}
      pt={[13, 11, 10, '120px']}
      sx={{ position: 'relative' }}
    >
      <Box
        id="join"
        ref={anchorRef}
        sx={{ position: 'absolute', top: '-60px' }}
      />
      <img
        src="/images/icons/can-club-flag.svg"
        alt="An arm holding a flag that reads 'Can Club Crew'"
        sx={{
          height: ['168px', '192px', '211px', '301px'],
          width: ['182px', '208px', '228px', '326px'],
          left: ['-30px', null, '-50px'],
          pointerEvents: 'none',
          position: 'absolute',
          top: ['-60px', '-20px', '62px', 11],
          animation: isVisible && `${slide} 0.25s ease-in 0s normal`,
          visibility: isVisible ? 'visible' : 'hidden',
        }}
      />
      <Container ref={membershipRef}>
        <Grid columns={[null, 12]} sx={{ rowGap: '0' }}>
          <Heading
            as="h2"
            variant="title2"
            mb={[5, 6, 7, 9]}
            sx={{ gridColumn: [null, '3 / 11'], textAlign: 'center' }}
          >
            {title}
          </Heading>
          <Grid
            columns={[1, 2]}
            mb={[4, 3, 4]}
            sx={{
              gridColumn: [null, '1 / -1', '2/ 12', '3 / 11'],
              rowGap: [4],
            }}
          >
            {media.map((subscription, index) => {
              const {
                presentation,
                defaultQuantity,
                displayPrice,
                features,
                uniqueFeatures,
              } = subscription

              return (
                <Flex
                  key={index}
                  bg="white"
                  sx={{
                    border: '1px solid',
                    borderColor: 'greyLight',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    bg="backgroundMedium"
                    py={2}
                    sx={{ textAlign: 'center' }}
                  >
                    <Heading as="h3" variant="title3" mb={1}>
                      {presentation}
                    </Heading>
                    <Text as="p" variant="description" color="greyDark">
                      {`(${getFormattedBottleCount(
                        defaultQuantity
                      )} bottle equivalent)`}
                    </Text>
                  </Box>
                  <Box p={[2, null, 3]}>
                    <ul
                      sx={{
                        borderBottom: '1px solid',
                        borderBottomColor: 'greyLight',
                        listStyleType: 'none',
                        m: '0',
                        p: '0',
                        pb: [2, 3],
                      }}
                    >
                      {uniqueFeatures.map((item, index) => {
                        return (
                          <Box as="li" key={index} mb={1}>
                            <Flex>
                              <Box sx={{ width: '32px' }}>
                                <CheckmarkIcon
                                  sx={{
                                    color: 'brandTertiary',
                                    position: 'relative',
                                    top: '-2px',
                                  }}
                                />
                              </Box>

                              <Text
                                as="p"
                                m={0}
                                variant="body2"
                                sx={{
                                  fontWeight: 'medium',
                                  textAlign: 'center',
                                }}
                              >
                                {item}
                              </Text>
                            </Flex>
                          </Box>
                        )
                      })}
                      {features.map((feature, index) => {
                        return (
                          <Box
                            as="li"
                            key={index}
                            sx={{
                              '&:not(:last-of-type)': { mb: 1 },
                            }}
                          >
                            <Flex>
                              <Box sx={{ width: '32px' }}>
                                <CheckmarkThinIcon
                                  sx={{
                                    color: 'brandTertiary',
                                    left: '1px',
                                    position: 'relative',
                                    top: '-2px',
                                  }}
                                />
                              </Box>
                              <Text as="p" m={0} variant="body2">
                                {feature}
                              </Text>
                            </Flex>
                          </Box>
                        )
                      })}
                    </ul>
                    <Flex sx={{ flexDirection: 'column' }}>
                      <Heading
                        as="h4"
                        mb="16px"
                        pt={[3, 2, 3]}
                        variant="title6"
                        sx={{ textAlign: 'center' }}
                      >{`Starts at ${toUsdCurrency(
                        displayPrice
                      )} per quarter`}</Heading>

                      <Button
                        as={Link}
                        to={`/can-club/join/${defaultQuantity}/`}
                      >
                        {`Join The ${defaultQuantity} Can Club`}
                      </Button>
                    </Flex>
                  </Box>
                </Flex>
              )
            })}
          </Grid>

          <Box
            variant="text.description"
            color="greyDark"
            sx={{
              gridColumn: [null, '2 / 12'],
              textAlign: 'center',
            }}
            dangerouslySetInnerHTML={{
              __html: footer.childMarkdownRemark.html,
            }}
          />
        </Grid>
      </Container>
    </Box>
  )
}

export default ChooseMembershipSection
